import { Typography } from "@avenue-8/sidekick-ui";
import { BrandingSectionView } from "@cma/presentation-generation-logic/models/section-view";
import { textFormatter } from "@shared/utils/text-formatter";
import { useMemo } from "react";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import { socialMediaConfig } from "./helpers/social-media-helpers";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import {
  AgentInfoContent,
  BrokerageInfoContainer,
  BrokerageLogoContainer,
  ContactInfoContainer,
  ContactInfoItem,
  SocialMediasContainer,
} from "./Branding.styles";
import {
  PrintPaper,
  PrintPage,
} from "@cma/chat-widgets/cma/components/shared/Print.styles";
import styled from "@emotion/styled";

const PrintContactCard = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: calc(100% - 2rem);
  margin: auto;
  gap: 2rem;
  align-items: center;
`;

const PrintLeftCardInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  transform: scale(1.2);
  transform-origin: left center;
`;

const PrintAgentAvatarContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  img {
    width: 100%;
    max-width: 400px;
    height: auto;
    border-radius: 0.5rem;
  }
`;

interface Props extends PresentationSectionViewProps<BrandingSectionView> {}

export const BrandingPrint = (props: Props) => {
  const { sectionView } = props;
  const {
    id,
    agentName,
    agentEmail,
    agentTitle,
    agentPhoneNumber,
    agentAvatar,
    officeName,
    officeLogo,
    website,
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
    preferences,
  } = sectionView;

  const {
    showAgentName,
    showAgentEmail,
    showAgentTitle,
    showOfficeName,
    showAgentPhoneNumber,
    showWebsite,
    showAgentAvatar,
  } = useMemo(() => {
    if (!preferences) return {};
    return preferences;
  }, [preferences]);

  const clickableUrl = textFormatter.formatClickableUrl(website ?? null);
  const prettyUrl = textFormatter.formatPrettyUrl(website ?? null);

  const socialMediaFields = {
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
  };

  return (
    <PrintPaper id={id}>
      <PrintPage>
        <PrintContactCard>
          <PrintLeftCardInfo>
            {(!!officeLogo || showOfficeName) && (
              <BrokerageInfoContainer>
                <BrokerageLogoContainer>
                  {!!officeLogo && (
                    <img
                      src={officeLogo}
                      alt={officeName ?? "Brokerage logo"}
                    />
                  )}
                </BrokerageLogoContainer>
                {showOfficeName && officeName && (
                  <Typography variant="h5">{officeName}</Typography>
                )}
              </BrokerageInfoContainer>
            )}

            <AgentInfoContent>
              {showAgentName && agentName && (
                <>
                  <Typography variant="h6">{agentName}</Typography>
                  {showAgentTitle && agentTitle && (
                    <Typography variant="body2">{agentTitle}</Typography>
                  )}
                </>
              )}

              <ContactInfoContainer>
                {showAgentEmail && agentEmail && (
                  <ContactInfoItem>
                    <MailIcon fontSize="small" />
                    <span>{agentEmail}</span>
                  </ContactInfoItem>
                )}

                {showAgentPhoneNumber && agentPhoneNumber && (
                  <ContactInfoItem>
                    <PhoneIcon fontSize="small" />
                    <span>
                      {textFormatter.formatPhoneNumber(agentPhoneNumber)}
                    </span>
                  </ContactInfoItem>
                )}

                {showWebsite && clickableUrl && prettyUrl && (
                  <ContactInfoItem>
                    <LanguageIcon fontSize="small" />
                    <span>{prettyUrl}</span>
                  </ContactInfoItem>
                )}
              </ContactInfoContainer>
            </AgentInfoContent>

            <SocialMediasContainer>
              {Object.entries(socialMediaConfig).map(
                ([key, { name, SvgIcon, extractHandle, handleToUrl }]) => {
                  const handle = extractHandle(socialMediaFields[name] || "");
                  const url = handleToUrl(handle || "");

                  if (!handle?.trim() || !url) return null;

                  return (
                    <ContactInfoItem key={key}>
                      <SvgIcon fontSize="small" />
                      <span>{handle}</span>
                    </ContactInfoItem>
                  );
                },
              )}
            </SocialMediasContainer>
          </PrintLeftCardInfo>

          {showAgentAvatar && agentAvatar && (
            <PrintAgentAvatarContainer>
              <img src={agentAvatar} alt={agentName ?? "Agent profile photo"} />
            </PrintAgentAvatarContainer>
          )}
        </PrintContactCard>
      </PrintPage>
    </PrintPaper>
  );
};
